import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus, AccountInfo } from '@azure/msal-browser';
import { AuthContextType, AdminUser } from '@/types';
import { apiTokenRequest } from './msalConfig';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function accountToUser(account: AccountInfo): AdminUser {
  return {
    id: account.localAccountId,
    email: account.username,
    name: account.name || account.username,
    role: 'super_admin',
    createdAt: new Date().toISOString(),
    isActive: true,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const isMsalAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState<AdminUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // On page load / refresh: restore session from cached MSAL account
  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;

    // Already have a token (e.g. from loginPopup) — skip
    if (token) {
      setLoading(false);
      return;
    }

    if (isMsalAuthenticated && accounts.length > 0) {
      const account = accounts[0];
      instance
        .acquireTokenSilent({ ...apiTokenRequest, account })
        .then((response) => {
          setUser(accountToUser(account));
          setToken(response.accessToken);
        })
        .catch(() => {
          setToken(null);
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setUser(null);
      setToken(null);
      setLoading(false);
    }
  }, [isMsalAuthenticated, accounts, inProgress, instance, token]);

  const login = useCallback(async () => {
    await instance.loginRedirect(apiTokenRequest);
  }, [instance]);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  }, [instance]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!accounts.length) return null;
    try {
      const response = await instance.acquireTokenSilent({
        ...apiTokenRequest,
        account: accounts[0],
      });
      setToken(response.accessToken);
      return response.accessToken;
    } catch {
      return null;
    }
  }, [instance, accounts]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!user && !!token,
        user,
        token,
        loading: loading || inProgress !== InteractionStatus.None,
        login,
        logout,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
